import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import type { AgingBucket, DebtAging } from '../../types/billing';
import { formatCurrency, formatDate } from '../../utils/format';
import PendingDecision from '../ui/PendingDecision';

const BUCKETS: AgingBucket[] = ['not_due', 'd0_30', 'd31_60', 'd61_90', 'd90_plus'];

/**
 * Sequential severity scale (DESIGN_SYSTEM.md §2.4 — severity is a scale, not a
 * rainbow): not yet due is neutral, then warning → danger as the debt ages.
 */
const BAR: Record<AgingBucket, string> = {
  not_due: 'bg-surface-300',
  d0_30: 'bg-warning-300',
  d31_60: 'bg-warning-500',
  d61_90: 'bg-danger-400',
  d90_plus: 'bg-danger-600',
};

/**
 * Decision 121 — receivables aging: OUTSTANDING amounts (after partial
 * payments and adjustments) by days past the due date. Server-side gated by
 * the financial permission; renders nothing when the caller lacks it.
 *
 * Presentation: one stacked share bar, a quiet legend that carries every
 * figure (neutral; only the 90+ bucket is tinted), and a per-customer table.
 */
export default function DebtAgingPanel({ organizationId }: { organizationId?: string }) {
  const { t } = useTranslation();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['debt-aging', organizationId ?? ''],
    queryFn: () => api.getDebtAging(organizationId ? { organization_id: organizationId } : undefined).then((r) => r.data as DebtAging),
    retry: false,
  });

  if (isError) return null;
  if (isLoading || !data) {
    return (
      <section className="card space-y-4" aria-busy="true">
        <div className="skeleton h-5 w-32" />
        <div className="skeleton h-2 w-full rounded-full" />
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {BUCKETS.map((b) => <div key={b} className="skeleton h-10" />)}
        </div>
      </section>
    );
  }

  const total = Number(data.totals.total ?? 0);
  const overdue = BUCKETS.filter((b) => b !== 'not_due').reduce((s, b) => s + Number(data.totals[b] ?? 0), 0);

  return (
    <section className="bg-surface-0 border border-surface-200 rounded-xl" aria-labelledby="aging-title">
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 id="aging-title" className="text-base font-semibold text-surface-900">{t('aging.title')} <PendingDecision id="PM-07" /></h2>
            <p className="text-xs text-surface-500 mt-0.5">{t('aging.asOf', { date: formatDate(data.as_of) })}</p>
          </div>
          <div className="text-end">
            <p className="text-xs text-surface-500">{t('aging.total')}</p>
            <p className="text-xl font-semibold text-surface-900 tabular-nums">{formatCurrency(total)}</p>
            {overdue > 0 && (
              <p className="text-xs text-danger-700 tabular-nums">{t('uxFinance.aging.overdueOf', { amount: formatCurrency(overdue) })}</p>
            )}
          </div>
        </div>

        {/* Share of the open balance per age bucket (decorative — the legend carries every figure). */}
        <div className="flex h-2 w-full overflow-hidden rounded-full bg-surface-100 gap-px" aria-hidden="true">
          {total > 0 && BUCKETS.map((b) => {
            const v = Number(data.totals[b] ?? 0);
            if (v <= 0) return null;
            return <div key={b} className={`${BAR[b]} h-full`} style={{ width: `${(v / total) * 100}%` }} />;
          })}
        </div>

        <dl className="grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-3">
          {BUCKETS.map((b) => {
            const v = Number(data.totals[b] ?? 0);
            return (
              <div key={b} className="min-w-0">
                <dt className="flex items-center gap-1.5 text-xs text-surface-600">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${BAR[b]}`} aria-hidden="true" />
                  {t(`aging.buckets.${b}`)}
                </dt>
                <dd className={`mt-0.5 text-base font-medium tabular-nums ${
                  v <= 0 ? 'text-surface-500' : b === 'd90_plus' ? 'text-danger-700' : 'text-surface-900'
                }`}>
                  {formatCurrency(v)}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>

      {data.organizations.length > 0 && (
        <div className="border-t border-surface-200 overflow-x-auto">
          <table className="table-modern">
            <caption className="sr-only">{t('uxFinance.aging.byCustomer')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('billing.col_customer')}</th>
                {BUCKETS.map((b) => (
                  <th key={b} scope="col" className="!text-end hidden md:table-cell">{t(`aging.buckets.${b}`)}</th>
                ))}
                <th scope="col" className="!text-end">{t('aging.total')}</th>
              </tr>
            </thead>
            <tbody>
              {data.organizations.map((o) => (
                <tr key={o.organization_id}>
                  <td className="font-medium text-surface-900">{o.organization_name}</td>
                  {BUCKETS.map((b) => (
                    <td key={b} className={`text-end tabular-nums hidden md:table-cell ${
                      !o[b] ? 'text-surface-500' : b === 'd90_plus' ? 'text-danger-700' : 'text-surface-800'
                    }`}>
                      {o[b] ? formatCurrency(o[b]) : '—'}
                    </td>
                  ))}
                  <td className="text-end tabular-nums font-medium text-surface-900">{formatCurrency(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
